import CircularProgress from "@mui/material/CircularProgress";
import { useContext, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { removeAllFormstates } from "~/src/contexts/StorageContext/helpers";
import { useStateful } from "~/src/hooks/useStateful";
import AppDataContext from "../../contexts/AppDataContext/DataProvider";
import useRouteProps from "../../hooks/useRouteProps";
import useScrollShadow from "../../hooks/useScrollShadow";
import ErrorPage from "../Error/ErrorPage";
import MinSide from "../MinSide/MinSideLayout";
import OversiktLayout from "../Oversikt/OversiktLayout";
import Registrering from "../Registrering/Registrering";
import NavigationDefault from "./components/BottomNavigation/NavigationDefault";
import CardHeaderDefault from "./components/CardHeader/CardHeaderDefault";

export default function MainLayout(): JSX.Element {
  const appData = useContext(AppDataContext);
  const pathProps = useRouteProps();
  const dynamicTitle = useStateful<string>(pathProps.pageTitle ?? "");
  const containerRef = useRef<HTMLDivElement>(null);
  const heightRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    handleScroll,
    BottomShadow,
    TopShadow,
    updatePosition: updateShadowPosition,
  } = useScrollShadow({ containerRef, navbarRef, staticShadow: pathProps.staticShadow });

  useEffect(() => {
    if (!pathProps.keepDraftPage && appData.draftExists.value) {
      //Removes registration drafts if there are any
      removeAllFormstates();

      appData.draftExists.set(false);
    }

    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes("android") && userAgent.includes("chrome")) {
      //android chrome calculates height differently
      const bod = document.getElementById("body");
      bod?.setAttribute("style", "maxHeight: 100dvh; overflow: clip;");
    }

    updateShadowPosition();

    const container = containerRef.current;
    if (!container) return;
    handleScroll();
    container.addEventListener("scroll", handleScroll);

    const heightContainer = heightRef.current;
    if (!heightContainer) return;

    const heightObserver = new ResizeObserver(() => {
      setTimeout(() => {
        handleScroll();
      }, 100);
    });

    heightObserver.observe(heightContainer);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [location]);

  useEffect(() => {
    if (
      location.pathname != "/velkommen" &&
      appData.jegeropplysninger?.isSuccess &&
      !appData.jegeropplysninger.data?.HarGyldigProfil
    ) {
      navigate("/velkommen");
    }
  }, [
    location.pathname,
    navigate,
    appData.jegeropplysninger?.isSuccess,
    appData.jegeropplysninger?.data?.HarGyldigProfil,
  ]);

  if (appData.jegeropplysninger?.data === undefined || appData.jaktområder?.data === undefined) {
    return (
      <div
        aria-label="Laster"
        className="w-full bg-ldir-brown-200 h-screen flex flex-col justify-between items-center animate-pulse"
      >
        <CardHeaderDefault pathProps={{}} />
        <CircularProgress thickness={7} size={80} />
        <span className="w-full h-20 bg-ldir-blue-900" />
      </div>
    );
  }

  function setDynamicContent(header: string) {
    dynamicTitle.set(header);
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      const target = container.querySelector("#bottom-bar");

      if (target) {
        updateShadowPosition();
        observer.disconnect();
      }
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }
  const maxH = pathProps.hideNavbar ? "max-h-[100vh]" : "max-h-[93vh]";

  return (
    <div className="flex flex-col items-center" style={{ height: "var(--app-vh)" }}>
      <CardHeaderDefault pathProps={pathProps} dynamicTitle={dynamicTitle.value} />
      <div ref={containerRef} className={`${maxH} overflow-y-auto w-full h-full`}>
        <div ref={heightRef} className={` w-full min-h-fit h-full`}>
          {TopShadow}
          <Routes>
            <Route path="registrering/*" element={<Registrering />} />
            <Route path="oversikt/*" element={<OversiktLayout setHeader={setDynamicContent} />} />
            <Route path="minside/*" element={<MinSide />} />
            <Route path="/" element={<Navigate to="/oversikt" replace />} />
            <Route path="/*" element={<ErrorPage />} />
          </Routes>
          {BottomShadow}
        </div>
      </div>
      {!pathProps.hideNavbar && (
        <div className="w-dvw max-w-244" ref={navbarRef}>
          <NavigationDefault id="mainPageNavigation" />
        </div>
      )}
    </div>
  );
}
